import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "@/lib/auth";
import { LayoutDashboard, Building2, Key, ScrollText, LogOut, Shield, MessageCircle, LayoutTemplate, Ticket, UserRound, Search, ImageIcon, Puzzle, Layers, Handshake, Newspaper, Languages, Wallet, Bot, Facebook, ShieldOff, Megaphone, Receipt, Palette, ShieldAlert, ShoppingCart, Target } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";

const NAV = [
  { to: "/super-admin", icon: LayoutDashboard, label: "Overview", testId: "snav-dashboard", end: true },
  { to: "/super-admin/tenants", icon: Building2, label: "Tenants", testId: "snav-tenants" },
  { to: "/super-admin/theme-settings", icon: Palette, label: "Theme Settings", testId: "snav-theme-settings" },
  { to: "/super-admin/adult-control", icon: ShieldAlert, label: "Adult Control", testId: "snav-adult-control" },
  { to: "/super-admin/sales-users", icon: Handshake, label: "Sales users", testId: "snav-sales-users" },
  { to: "/super-admin/sales-targets", icon: Target, label: "Sales targets", testId: "snav-sales-targets" },
  { to: "/super-admin/facebook", icon: Facebook, label: "Facebook Setup", testId: "snav-facebook" },
  { to: "/super-admin/google-ads", icon: Megaphone, label: "Google Ads", testId: "snav-google-ads" },
  { to: "/super-admin/fb-deletion", icon: ShieldOff, label: "Fb Deletion Request", testId: "snav-fb-deletion" },
  { to: "/super-admin/purchase-billing", icon: Receipt, label: "Purchase & billing", testId: "snav-purchase-billing" },
  { to: "/super-admin/abandoned-cart", icon: ShoppingCart, label: "Abandoned Cart", testId: "snav-abandoned-cart" },
  { to: "/super-admin/addons", icon: Puzzle, label: "Paid Addons", testId: "snav-addons" },
  { to: "/super-admin/bulk-addons", icon: Layers, label: "Bulk addons", testId: "snav-bulk-addons" },
  { to: "/super-admin/support", icon: MessageCircle, label: "Support", testId: "snav-support", badgeKey: "support" },
  { to: "/super-admin/support-ai", icon: Bot, label: "Support AI", testId: "snav-support-ai" },
  { to: "/super-admin/integrations", icon: Key, label: "Integrations", testId: "snav-integrations" },
  { to: "/super-admin/media", icon: ImageIcon, label: "Media URLs", testId: "snav-media" },
  { to: "/super-admin/landing", icon: LayoutTemplate, label: "Landing page", testId: "snav-landing" },
  { to: "/super-admin/seo", icon: Search, label: "SEO & Marketing", testId: "snav-seo" },
  { to: "/super-admin/blog", icon: Newspaper, label: "Blog", testId: "snav-blog" },
  { to: "/super-admin/translation", icon: Languages, label: "Translation", testId: "snav-translation" },
  { to: "/super-admin/wallet", icon: Wallet, label: "Wallet & Referal", testId: "snav-wallet" },
  { to: "/super-admin/coupons", icon: Ticket, label: "Signup coupons", testId: "snav-coupons" },
  { to: "/super-admin/leads", icon: UserRound, label: "Chatbot leads", testId: "snav-leads" },
  { to: "/super-admin/audit", icon: ScrollText, label: "Audit logs", testId: "snav-audit" },
];

export default function SuperLayout() {
  const { user, logout } = usePlatformAuth();
  const nav = useNavigate();
  const [supportUnread, setSupportUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/platform/support/unread_count").then(({ data }) => { if (alive) setSupportUnread(data.unread || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const Sidebar = ({ onNav }) => (
    <div className="w-full bg-[#090D16] text-slate-100 flex flex-col h-full">
      <div className="p-5 border-b border-slate-800/80 flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center"><Shield className="w-4 h-4 text-indigo-300" /></div>
        <div>
          <div className="font-display font-extrabold text-sm">Platform</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Command center</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto dash-scroll-dark">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testId} onClick={onNav} className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-indigo-500/15 text-indigo-200 border border-indigo-400/20" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="w-4 h-4" />
            <span className="flex-1">{n.label}</span>
            {n.badgeKey === "support" && supportUnread > 0 && (
              <span className="min-w-[20px] h-5 rounded-full bg-orange-500 text-slate-900 text-[11px] font-bold flex items-center justify-center px-1.5" data-testid="snav-support-badge">{supportUnread}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800/80">
        <div className="px-3 py-2 text-xs text-slate-500 truncate">{user?.email}</div>
        <button onClick={async () => { await logout(); toast.success("Signed out"); nav("/super-admin/login"); }} data-testid="super-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-white/5"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );

  return (
    <DashboardShell
      menuTestId="super-mobile-menu"
      shellClassName="bg-[#090D16] text-slate-100"
      asideClassName="bg-[#090D16] border-r border-slate-800/80"
      asideWidthClass="w-60"
      mobileBarClassName="bg-[#090D16] text-slate-100 border-slate-800/80"
      menuButtonClassName="text-slate-100 hover:bg-white/10"
      sheetClassName="p-0 w-64 max-w-[85vw] bg-[#090D16] border-slate-800"
      renderSidebar={(onNav) => <Sidebar onNav={onNav} />}
      brand={(
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center shrink-0"><Shield className="w-4 h-4 text-indigo-300" /></div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-sm truncate">Platform</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Command center</div>
          </div>
        </div>
      )}
    >
      <Outlet />
    </DashboardShell>
  );
}
